import { z } from 'zod'

/**
 * Zod schema for lease contract form validation.
 * Used by both drawer (full) and inline (quick-edit) variants.
 */
export const leaseFormSchema = z.object({
  location_id: z.string().min(1, 'Location is required'),
  client_id: z.string().min(1, 'Client is required'),
  contact_id: z.string().optional().nullable(),
  unit_id: z.string().min(1, 'Unit is required'),
  type: z.enum(['long_term', 'month_to_month', 'seasonal'] as const),
  status: z.enum([
    'draft',
    'pending_signature',
    'active',
    'expiring',
    'expired',
    'terminated',
    'completed',
    'cancelled',
  ] as const),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional().nullable(),
  term_months: z.coerce.number().optional().nullable(),
  monthly_rate: z.coerce.number().min(0, 'Rate must be positive'),
  rate_basis: z
    .enum(['flat', 'per_sqft', 'per_night'] as const)
    .optional()
    .nullable(),
  security_deposit: z.coerce.number().optional().nullable(),
  escalation_type: z
    .enum(['none', 'fixed_pct', 'cpi'] as const)
    .optional()
    .nullable(),
  escalation_pct: z.coerce.number().optional().nullable(),
  escalation_date: z.string().optional().nullable(),
  auto_renew: z.boolean().default(false),
  renewal_notice_days: z.coerce.number().optional().nullable(),
  allow_transient_sublease: z.boolean().default(false),
  transient_revenue_share_pct: z.coerce.number().optional().nullable(),
  // Fuel allowance
  fuel_type: z
    .enum(['jet_a', 'avgas'] as const)
    .optional()
    .nullable(),
  fuel_commitment_gallons_monthly: z.coerce.number().int().optional().nullable(),
  fuel_commitment_gallons_annual: z.coerce.number().int().optional().nullable(),
  fuel_price_per_gallon: z.coerce.number().optional().nullable(),
  fuel_price_type: z
    .enum(['fixed', 'opis_plus', 'retail_discount'] as const)
    .optional()
    .nullable(),
  fuel_price_differential: z.coerce.number().optional().nullable(),
  placement_guarantee: z.boolean().default(true),
  requires_unblocked_access: z.boolean().default(false),
  document_url: z.string().optional().nullable(),
  signed_at: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
})

// Input type — used for form defaultValues
export type LeaseFormInput = z.input<typeof leaseFormSchema>

// Output type — after Zod parsing/coercion
export type LeaseFormValues = z.output<typeof leaseFormSchema>

// Default values for new lease form
const _today = new Date()
const _oneYearLater = new Date(_today)
_oneYearLater.setFullYear(_oneYearLater.getFullYear() + 1)

export const leaseFormDefaults: LeaseFormInput = {
  location_id: '',
  client_id: '',
  contact_id: null,
  unit_id: '',
  type: 'long_term',
  status: 'draft',
  start_date: _today.toISOString().split('T')[0],
  end_date: _oneYearLater.toISOString().split('T')[0],
  term_months: 12,
  monthly_rate: 0,
  rate_basis: 'flat',
  security_deposit: null,
  escalation_type: 'none',
  escalation_pct: null,
  escalation_date: null,
  auto_renew: false,
  renewal_notice_days: 30,
  allow_transient_sublease: false,
  transient_revenue_share_pct: null,
  fuel_type: null,
  fuel_commitment_gallons_monthly: null,
  fuel_commitment_gallons_annual: null,
  fuel_price_per_gallon: null,
  fuel_price_type: null,
  fuel_price_differential: null,
  placement_guarantee: true,
  requires_unblocked_access: false,
  document_url: null,
  signed_at: null,
  notes: null,
}
